// Complete documenation available here: /docs/cusa-dialog.md
/**
 * @file cusa-dialog.js
 * @description Defines the <cusa-dialog> custom element. Handles modal dialog support with fallback to
 * collapsible sections for unsupported browsers. Includes accessibility enhancements and focus trapping.
 * Usage details in /docs/cusa-dialog.md
 */
if (!window.CusaDialog) {
	console.log('✅ Loaded latest CusaDialog definition');
	/**
	 * <cusa-dialog> Web Component
	 * Supports accessible modal dialogs with fallback collapsible regions when dialogs are not supported.
	 * Automatically attaches triggers via data-dialog-trigger and manages ARIA, IDs, and focus handling.
	 * @extends HTMLElement
	 */
	class CusaDialog extends HTMLElement {
		/**
		 * Custom dialog element that manages modal dialog behavior,
		 * accessibility, and focus trapping.
		 */
		constructor() {
			super();
			/** @type {HTMLDialogElement|null} Reference to the dialog element */
			this.dialog = null;
			/** @type {HTMLElement|null} Element that was focused before dialog opened */
			this.previouslyFocused = null;
			/** @type {HTMLElement[]} Array of focusable elements inside the dialog */
			this.focusableEls = [];
			this.isCollapsible = false;
			this.supportsDialog = 'HTMLDialogElement' in window;
		}

		/**
		 * Lifecycle method called when the element is added to the DOM.
		 * Initializes dialog structure, IDs, event listeners, and accessibility.
		 * @private
		 */
		connectedCallback() {
			// ID handling and randomization for uniqueness
			const baseId = this.getAttribute('id');

			// Check for duplicate base IDs
			if (baseId) {
				const duplicates = document.querySelectorAll(`[id="${baseId}"]`);
				if (duplicates.length > 1) {
					console.warn(
						`Warning: Multiple elements with the same base ID "${baseId}" detected. This may cause unexpected trigger behavior.`
					);
				}
			}

			const randomSuffix = Math.random().toString(36).slice(2, 8);
			const preferModal = this.supportsDialog;
			const finalId = preferModal
				? baseId
					? `${baseId}--${randomSuffix}`
					: `cusa-dialog--${randomSuffix}`
				: baseId || `cusa-dialog--${randomSuffix}`;

			// Assign final ID and traceability attributes
			if (!this.hasAttribute('id')) {
				this.setAttribute('id', finalId);
			}
			this.setAttribute('data-dialog-id', finalId);
			if (baseId) this.setAttribute('data-base-id', baseId);

			// Update all triggers that referenced the baseId to use the new dialog id
			if (baseId) {
				document.querySelectorAll(`[data-dialog-trigger="${baseId}"]`).forEach((trigger) => {
					trigger.setAttribute('data-dialog-trigger', finalId);
				});
			}

			this.isCollapsible = !this.supportsDialog && this.getAttribute('mode') === 'collapsible';

			// Template injection: wrap content in <dialog> or <div> with close button and body container, based on mode
			const content = this.innerHTML;

			const wrapperTag = preferModal ? 'dialog' : 'div';
			const wrapperAttributes = preferModal
				? `role="dialog" aria-modal="true" aria-labelledby="${finalId}-label"`
				: `role="region" aria-labelledby="${finalId}-label"`;

			this.innerHTML = `
      <${wrapperTag} ${wrapperAttributes}>
        ${
					!this.isCollapsible
						? `
        <div class="cusa-dialog-close-btn-wrapper">
          <button class="cusa-dialog-close-btn" aria-label="Close">×</button>
        </div>`
						: ''
				}
        <div class="cusa-dialog-body" tabindex="-1">${content}</div>
      </${wrapperTag}>
    `;

			this.dialog = this.querySelector(wrapperTag);
			// Always start hidden and remove later in openDialog or toggleCollapsible
			this.classList.add('hidden');

			if (this.isCollapsible) {
				this.dialog.setAttribute('role', 'region');
				this.dialog.setAttribute('aria-labelledby', `${finalId}-label`);

				const triggers = document.querySelectorAll(`[aria-controls="${finalId}"]`);
				console.log('🔍 Looking for triggers with selector:', `[aria-controls="${finalId}"]`);
				if (triggers.length > 0) {
					triggers.forEach((trigger, index) => {
						// Ensure trigger has an ID
						if (!trigger.id) {
							trigger.id = `${finalId}-trigger-${index}`;
						}
						trigger.setAttribute('aria-expanded', 'false');
						trigger.setAttribute('tabindex', '0');
						console.log('📌 Adding click and keyboard listener to:', trigger);
						const toggleHandler = (e) => {
							if (e.type === 'click' || e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								this.toggleCollapsible();
							}
						};
						trigger.addEventListener('click', toggleHandler);
						trigger.addEventListener('keydown', toggleHandler);
					});
					this.trigger = triggers[0]; // Keep reference to first one for aria-expanded syncing
					this.dialog.setAttribute('aria-labelledby', this.trigger.id);
				} else {
					console.warn('⚠️ No triggers found for:', finalId);
				}

				const closeBtn = document.createElement('button');
				closeBtn.className = 'cusa-dialog-close-btn';
				closeBtn.textContent = 'Close';
				closeBtn.setAttribute('type', 'button');
				closeBtn.addEventListener('click', () => {
					this.toggleCollapsible();
					if (this.trigger) this.trigger.focus();
				});
				this.dialog.appendChild(closeBtn);
				return; // Skip modal setup if collapsible
			}

			const closeBtn = this.querySelector('.cusa-dialog-close-btn');
			const body = this.querySelector('.cusa-dialog-body');

			// ARIA label handling: assign ID to first heading for aria-labelledby
			const heading = body.querySelector('h1, h2, h3, h4, h5, h6');
			if (heading && !heading.id) heading.id = `${finalId}-label`;

			// Auto-open if `open` attribute present
			if (this.hasAttribute('open') && this.dialog.showModal) {
				this.openDialog();
			}

			// Add click listeners for all matching triggers; handle anchor fallback and modal open
			this.setupTriggerListeners(finalId);

			// Close button event listener
			closeBtn.addEventListener('click', () => this.closeDialog());

			// Clicking outside the dialog closes it
			this.dialog.addEventListener('click', (e) => {
				const rect = this.dialog.getBoundingClientRect();
				const outside =
					e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom;
				if (outside) this.closeDialog();
			});

			// Accessibility teardown on dialog close
			this.dialog.addEventListener('close', () => this.teardownAccessibility());

			// Prevent default cancel behavior and close dialog instead
			this.dialog.addEventListener('cancel', (e) => {
				e.preventDefault();
				this.closeDialog();
			});

			// Keyboard navigation: trap focus within dialog
			this.dialog.addEventListener('keydown', (e) => this.handleFocusTrap(e));
		}

		/**
		 * Lifecycle method called when the element is removed to the DOM.
		 * @private
		 */
		disconnectedCallback() {}

		/**
		 * Opens the dialog modal, sets focus, and applies accessibility states.
		 */
		openDialog() {
			if (this.isCollapsible) return;
			if (!this.dialog.open) this.dialog.showModal();
			this.previouslyFocused = document.activeElement;
			this.removeAttribute('style');
			this.classList.remove('hidden');
			this.setAttribute('open', '');
			this.setAccessibilityState();
			this.focusableEls = this.getFocusableElements();
			this.focusFirst();
			document.body.style.overflow = 'hidden';
		}

		/**
		 * Closes the dialog modal and removes the open attribute.
		 */
		closeDialog() {
			if (this.isCollapsible) return;
			this.dialog.close();
			this.removeAttribute('open');
			document.body.style.overflow = '';
		}

		/**
		 * Applies accessibility attributes to hide all other page content
		 * from assistive technologies when the dialog is open.
		 * @private
		 */
		setAccessibilityState() {
			if (this.isCollapsible) return;
			document.querySelectorAll('body > *:not(cusa-dialog)').forEach((el) => {
				el.setAttribute('inert', '');
				el.setAttribute('aria-hidden', 'true');
			});
		}

		/**
		 * Removes accessibility attributes and restores focus to the previously focused element.
		 * @private
		 */
		teardownAccessibility() {
			if (this.isCollapsible) return;
			document.querySelectorAll('[inert], [aria-hidden]').forEach((el) => {
				el.removeAttribute('inert');
				el.removeAttribute('aria-hidden');
			});
			if (this.previouslyFocused) this.previouslyFocused.focus();
		}

		/**
		 * Retrieves all focusable elements within the dialog that are visible and enabled.
		 * @returns {HTMLElement[]} Array of focusable elements inside the dialog.
		 * @private
		 */
		getFocusableElements() {
			return Array.from(
				this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
			).filter((el) => !el.disabled && el.offsetParent !== null);
		}

		/**
		 * Sets focus to the first focusable element inside the dialog.
		 * @private
		 */
		focusFirst() {
			const el = this.focusableEls[0];
			if (el) el.focus();
		}

		/**
		 * Handles keyboard navigation to trap focus within the dialog while open.
		 * @param {KeyboardEvent} e - The keyboard event triggered on keydown.
		 * @private
		 */
		handleFocusTrap(e) {
			if (this.isCollapsible) return;
			if (e.key !== 'Tab') return;
			const focusables = this.focusableEls;
			if (focusables.length === 0) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];

			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		}

		/**
		 * Adds event listeners to all trigger elements matching the dialog ID.
		 * Supports both native <dialog> behavior and fallback to collapsible mode or anchor links.
		 * @param {string} finalId - The final unique ID for the dialog.
		 * @private
		 */
		setupTriggerListeners(finalId) {
			const preferModal = this.supportsDialog;
			if (preferModal) {
				document.querySelectorAll(`[data-dialog-trigger="${finalId}"]`).forEach((trigger) => {
					trigger.addEventListener('click', (e) => {
						const isDialogSupported = 'HTMLDialogElement' in window && this.dialog?.showModal;
						if (isDialogSupported) {
							e.preventDefault();
							this.openDialog();
						}
					});
				});
			} else {
				// Fallback: toggle collapsible or open anchor in new tab
				document.querySelectorAll(`[data-dialog-trigger="${finalId}"]`).forEach((trigger) => {
					const isAnchor = trigger.tagName.toLowerCase() === 'a';
					const clickHandler = (e) => {
						if (isAnchor) {
							e.preventDefault();
							window.open(trigger.href, '_blank');
						} else {
							e.preventDefault();
							this.toggleCollapsible();
						}
					};
					trigger.addEventListener('click', clickHandler);
				});
			}
		}

		/**
		 * Toggles visibility of the collapsible dialog fallback mode.
		 * Updates ARIA states and restores focus when closing via Escape.
		 * @private
		 */
		toggleCollapsible() {
			console.log('🔁 toggleCollapsible() called');
			const isOpen = this.hasAttribute('open');
			if (isOpen) {
				this.removeAttribute('open');
				this.classList.add('hidden');
				if (this.trigger) {
					this.trigger.setAttribute('aria-expanded', 'false');
				}
			} else {
				this.setAttribute('open', '');
				this.classList.remove('hidden');
				const onEsc = (e) => {
					const anyModalOpen = document.querySelector('dialog[open]');
					if (e.key === 'Escape' && !anyModalOpen) {
						this.toggleCollapsible();
						document.removeEventListener('keydown', onEsc);
						if (this.trigger) this.trigger.focus();
					}
				};
				document.addEventListener('keydown', onEsc);
				if (this.trigger) {
					this.trigger.setAttribute('aria-expanded', 'true');
				}
			}
		}
	}

	window.CusaDialog = CusaDialog;

	// Prevents "already defined" errors during hot reload in local Shopify development.
	if (!customElements.get('cusa-dialog')) {
		customElements.define('cusa-dialog', CusaDialog);
		// Add '?v=dev' or similar to your script URL in Liquid to force cache busting if changes don’t show.
	}

	// CSS suggestion:
	// [mode="collapsible"][hidden] { display: none; }
}
